// ─────────────────────────────────────────────────────────────────────────────
// Names — the one place a person, a branch, a department or a position gets
// turned into something to display.
//
// THE SERVER SENDS WHAT IT HAS, NOT A DECISION.
//
// Every payload used to carry a flat `${firstName} ${lastName}`, composed on
// the server from the English columns before the browser's language was known.
// Switching the interface to Arabic could not undo that — the choice had
// already been made in the response, so an Arabic page listed English names.
// `first_name_ar` and `last_name_ar` were sitting on the row, entered on the
// Contract page, read almost nowhere.
//
// This is the same problem `renderBilingual` solved for outgoing mail, and the
// comment there says it best: the recipient's language is unknown, and
// choosing one for them is how a Filipino nurse received Arabic. A response is
// no different. Send both; let the reader's own locale pick.
//
// So every name-bearing payload carries a PAIR — `x` and `xAr` — and the
// browser resolves it with `pick()` from the i18n context, which already
// handles the fallback in both directions.
//
// FALLBACK IS THE LATIN NAME, UNCHANGED. Where no Arabic name has been
// entered, `nameAr` is null and `pick` falls back to the Latin one. It is
// never transliterated — a machine guess at how somebody spells their own
// name in Arabic is worse than the spelling they gave us — and never blanked,
// because a nameless row is unusable.
//
// Held by `scripts/audit.mjs` check 36 (`one-name-helper`). This is a
// two-lists problem of the kind docs/TWO-LISTS.md describes, and the second
// list was 44 entries long: 44 server sites composing a name inline, each free
// to forget the Arabic columns, and every one of them did.
// ─────────────────────────────────────────────────────────────────────────────

/** The columns a person's name is built from. Extra fields are ignored. */
import { INTL_LOCALE, type UiLocale } from "./intl";

export interface NameColumns {
  readonly firstName: string;
  readonly lastName: string;
  readonly firstNameAr?: string | null;
  readonly lastNameAr?: string | null;
}

/** A name in both languages. `ar` is null when none has been entered. */
export interface BilingualName {
  readonly name: string;
  readonly nameAr: string | null;
}

/** Trims and drops empties, so `"  "` counts as absent rather than as a name. */
function joined(parts: readonly (string | null | undefined)[]): string {
  return parts.map((p) => p?.trim() ?? "").filter((p) => p !== "").join(" ");
}

/** The Latin name. Always a string — this is the one that must never be empty. */
export function latinName(row: NameColumns): string {
  return joined([row.firstName, row.lastName]);
}

/**
 * The Arabic name, or null when it has not been entered.
 *
 * Deliberately tolerant of HALF a name: somebody with an Arabic first name and
 * no Arabic surname gets the half that exists rather than nothing. The
 * alternative — requiring both — is why several rows that DID carry an Arabic
 * first name still rendered entirely in Latin.
 */
export function arabicName(row: NameColumns): string | null {
  const joinedAr = joined([row.firstNameAr, row.lastNameAr]);
  return joinedAr === "" ? null : joinedAr;
}

/** Both, as the pair every payload should carry. */
export function personName(row: NameColumns): BilingualName {
  return { name: latinName(row), nameAr: arabicName(row) };
}

/**
 * The same pair under a caller's own field names — `employeeName` /
 * `employeeNameAr`, `ownerName` / `ownerNameAr`, and so on.
 *
 * A helper rather than two calls at each site, because the failure this whole
 * module exists to prevent is a site that emits one field and forgets the
 * other. Spreading a single object cannot do that.
 */
export function namedAs<K extends string>(
  prefix: K,
  row: NameColumns,
): { [P in K]: string } & { [P in `${K}Ar`]: string | null } {
  // The two halves are typed separately and deliberately: the Latin one is
  // never null, and a caller that treats it as nullable would start writing
  // fallbacks for a case that cannot happen.
  return {
    [prefix]: latinName(row),
    [`${prefix}Ar`]: arabicName(row),
  } as { [P in K]: string } & { [P in `${K}Ar`]: string | null };
}

// ── Sorting ─────────────────────────────────────────────────────────────────
//
// A list of Arabic names in English alphabetical order is not a sort anybody
// can use, and eight queries produced exactly that: `ORDER BY first_name`
// decided the order from the column the page was no longer showing.
//
// SORTING FOLLOWS THE NAME THAT IS DISPLAYED, which is why the comparator is
// here and applied in the browser rather than in SQL. Three reasons, in order:
//
//   1. Only the browser knows what is on the screen. `pick()` chooses per
//      locale, and the server would have to be told the locale to sort by the
//      same string — so a server sort is always sorting a proxy for what the
//      reader sees.
//   2. The database's collation is `en_US.UTF-8`, under which Arabic sorts
//      into no order at all — measured: عمر، ستي، عبد، بدر، علي. Fixing that
//      in SQL means `COLLATE "ar-x-icu"` on every ORDER BY, which depends on
//      the Postgres build shipping ICU and degrades SILENTLY back to nonsense
//      wherever the clause is forgotten.
//   3. `Intl.Collator` needs neither. Its order was compared row for row
//      against `ORDER BY … COLLATE "ar-x-icu"` on the real 31-name list and is
//      identical, so nothing is given up by moving it.
//
// The one thing a client sort cannot do is page. None of the eight queries has
// a LIMIT — every one returns the complete set — so that cost is not paid
// here. A LIST THAT GAINS PAGINATION MUST MOVE ITS SORT BACK INTO SQL, with
// the COLLATE clause, and this paragraph is the warning.
//
// MIXED SCRIPT: on an Arabic page, names with no Arabic form fall back to
// Latin, and ICU groups that whole block together at the END rather than
// interleaving it. Two people called "System Administrator" and "Jomalyn" land
// next to each other in a predictable place, which is the defensible answer —
// scattering them through the Arabic names by transliterated guess is not.
// On an English page every name is Latin, so the case does not arise.

/**
 * A collator for the reader's language.
 *
 * `numeric` so "Ward 2" precedes "Ward 10", and `sensitivity: "base"` so
 * أحمد and احمد — the same name typed with and without the hamza, and both
 * present in this database — sort together instead of into separate blocks.
 */
export function nameCollator(locale: UiLocale): Intl.Collator {
  return new Intl.Collator(INTL_LOCALE[locale], { numeric: true, sensitivity: "base" });
}

/**
 * Compares two bilingual names as the reader sees them.
 *
 * Takes the pair rather than the resolved string so the fallback rule lives in
 * one place: the Arabic name when there is one and the page is Arabic, the
 * Latin name otherwise.
 */
export function compareNames(locale: UiLocale, a: BilingualName, b: BilingualName): number {
  return nameCollator(locale).compare(shownName(locale, a), shownName(locale, b));
}

/** The string a reader in `locale` actually sees. Mirrors `pick()` in the UI. */
export function shownName(locale: UiLocale, value: BilingualName): string {
  return (locale === "ar" ? value.nameAr ?? value.name : value.name) || "";
}

/**
 * A thing whose own name is already stored in both languages — a branch, a
 * department, a leave type, a holiday.
 *
 * Same rule, different columns. It exists so those surfaces are covered by the
 * audit check too: Ali reported the identical symptom on branch, department
 * and position, and they were failing for the identical reason.
 */
export function bilingualName(row: { readonly name: string; readonly nameAr?: string | null }): BilingualName {
  return { name: row.name.trim(), nameAr: row.nameAr?.trim() === "" ? null : row.nameAr?.trim() ?? null };
}

// ─────────────────────────────────────────────────────────────────────────────
// SEARCHING a name.
//
// The third thing built on a name to be written in one language. Display was
// fixed, then the sort, and the search still matched Latin while the screen
// showed Arabic — somebody typed a name they could see and got nothing back.
//
// The rule is written here and mirrored in SQL by `ar_norm` (migration 0048),
// which is where a query has to apply it. Two implementations is a real risk
// and the reason both are pinned by `names.test.ts` against the same table of
// pairs: if they ever disagree, the disagreement is the bug.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Arabic forms that a person types interchangeably, folded to one.
 *
 * Written as pairs rather than a regex so the SQL `translate()` and this can be
 * read side by side and checked against each other.
 */
const SEARCH_FOLD: readonly (readonly [string, string])[] = [
  // The alef forms, including the wasla nobody types on purpose.
  ["أ", "ا"], ["إ", "ا"], ["آ", "ا"], ["ٱ", "ا"],
  // Alef maqsura: يحيى and يحيي are one name.
  ["ى", "ي"],
  // Teh marbuta: نورة and نوره are one name.
  ["ة", "ه"],
  /**
   * Arabic-Indic digits, so a code or an iqama typed in the other set still
   * finds its row.
   *
   * Written as code points, not as the characters. `audit.mjs` refuses an
   * Eastern Arabic digit in source and is right to — every figure this system
   * DISPLAYS is Latin. These are not displayed: they are input this function
   * accepts and folds away, and spelling them \u0660 says that out loud instead
   * of looking like the very thing the rule forbids.
   */
  ["\u0660", "0"], ["\u0661", "1"], ["\u0662", "2"], ["\u0663", "3"], ["\u0664", "4"],
  ["\u0665", "5"], ["\u0666", "6"], ["\u0667", "7"], ["\u0668", "8"], ["\u0669", "9"],
];

/** Marks that decorate a word without changing it, and are typed at random. */
const SEARCH_STRIP = /[ً-ْـ]/g;

/**
 * A value reduced to what a search should compare.
 *
 * Applied to BOTH the stored name and the term somebody typed — that is the
 * whole point, and applying it to one side only would fail exactly as often as
 * applying it to neither.
 */
export function foldForSearch(value: string): string {
  let out = value.toLowerCase().replace(SEARCH_STRIP, "");
  for (const [from, to] of SEARCH_FOLD) out = out.split(from).join(to);
  return out;
}

/** Whether any of `haystacks` contains `term`, both folded. */
export function matchesSearch(term: string, ...haystacks: readonly (string | null | undefined)[]): boolean {
  const needle = foldForSearch(term.trim());
  if (needle === "") return true;
  return haystacks.some((h) => h != null && foldForSearch(h).includes(needle));
}
